import React, { useMemo, useState } from 'react';
import { PRIORITY_LABEL, initials, fmtDate } from '../lib/constants';
import { ChevronLeft, ChevronRight, Plus, X, Tag } from 'lucide-react';

function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function sameDay(a, b) { return a && b && new Date(a).toDateString() === new Date(b).toDateString(); }

const PRESET_COLORS = [
  '#2563eb', // Blue
  '#db2777', // Pink
  '#16a34a', // Green
  '#ea580c', // Orange
  '#7c3aed', // Purple
  '#0284c7'  // Sky Blue
];

const getActivityStyle = (title) => {
  if (!title) return null;
  const lower = title.toLowerCase();
  if (lower.includes('seo')) {
    return { bg: '#2563eb', color: '#ffffff', tag: 'SEO' };
  }
  if (lower.includes('sm post') || lower.includes('sm_post') || lower.includes('social media') || lower.includes('instagram') || lower.includes('twitter') || lower.includes('facebook') || lower.includes('linkedin')) {
    return { bg: '#db2777', color: '#ffffff', tag: 'SM Post' };
  }
  if (lower.includes('blog') || lower.includes('article') || lower.includes('newsletter')) {
    return { bg: '#16a34a', color: '#ffffff', tag: 'Blog' };
  }
  if (lower.includes('geo') || lower.includes('local seo') || lower.includes('google maps') || lower.includes('geotag')) {
    return { bg: '#ea580c', color: '#ffffff', tag: 'GEO' };
  }
  return null;
};

export default function CalendarView({ 
  tasks, 
  onOpenTask, 
  onAddTask, 
  lists = [], 
  dateMarkings = [], 
  onUpdateMarking,
  marketingTags = [],
  onCreateTag,
  onEditTag,
  onDeleteTag
}) {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  
  // Tag Picker & Management State
  const [activeMarkDropdownDate, setActiveMarkDropdownDate] = useState(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  
  // Create tag fields
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#2563eb');
  
  // Edit tag fields
  const [editingTagId, setEditingTagId] = useState(null);
  const [editTagName, setEditTagName] = useState('');
  const [editTagColor, setEditTagColor] = useState('#2563eb');

  const gridStart = useMemo(() => {
    const s = new Date(cursor); s.setDate(1 - s.getDay()); return s;
  }, [cursor]);

  const cells = useMemo(() => {
    const arr = [];
    const s = new Date(gridStart);
    for (let i = 0; i < 42; i++) {
      const d = new Date(s); d.setDate(s.getDate() + i);
      arr.push({ date: d, tasks: tasks.filter((t) => t.due_date && sameDay(t.due_date, d)) });
    }
    return arr;
  }, [gridStart, tasks]);

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const handleOpenAddModal = (date) => {
    setSelectedDate(date);
    setNewTaskTitle('');
    if (lists.length > 0) {
      setSelectedListId(lists[0].id);
    }
    setShowAddModal(true);
  };

  const handleCreateTask = () => {
    if (!newTaskTitle.trim() || !selectedListId || !selectedDate) return;
    onAddTask(selectedDate, newTaskTitle, selectedListId);
    setShowAddModal(false);
  };

  // Tag Management Handlers
  const handleCreateTagClick = () => {
    if (!newTagName.trim()) return;
    onCreateTag(newTagName.trim(), newTagColor);
    setNewTagName('');
    setNewTagColor('#2563eb');
  };

  const handleStartEditTag = (tag) => {
    setEditingTagId(tag.id);
    setEditTagName(tag.name);
    setEditTagColor(tag.color);
  };

  const handleSaveEditTag = (tagId) => {
    if (!editTagName.trim()) return;
    onEditTag(tagId, editTagName.trim(), editTagColor);
    setEditingTagId(null);
  };

  return (
    <div style={{ padding: 24 }} data-testid="calendar-view">
      <div className="flex items-center gap-4 mb-4" style={{ color: '#fff' }}>
        <button className="tm-btn-ghost-light" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20 }}>{monthLabel}</h2>
        <button className="tm-btn-ghost-light" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next"><ChevronRight size={16} /></button>
        <button className="tm-btn-ghost-light" onClick={() => setCursor(startOfMonth(new Date()))}>Today</button>
      </div>

      {/* Dynamic Marketing Activity Legend */}
      <div className="flex gap-4 mb-4 flex-wrap p-3.5 rounded-xl bg-white border border-slate-200 shadow-sm text-xs font-bold text-slate-700 items-center">
        <span className="text-slate-400 uppercase tracking-wider text-[10px] mr-2 flex items-center">Marketing Activities:</span>
        {marketingTags.map(tag => (
          <div key={tag.id} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: tag.color }} />
            <span>{tag.name}</span>
          </div>
        ))}
        <button
          onClick={() => setManageModalOpen(true)}
          className="ml-auto px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-[9px] font-extrabold uppercase tracking-wider transition-colors"
        >
          Manage / Create Tags
        </button>
      </div>

      <div style={{ background: 'var(--trello-list-bg)', borderRadius: 8, overflow: 'hidden' }}>
        <div className="grid grid-cols-7" style={{ background: 'var(--trello-list-bg)', fontSize: 12, fontWeight: 600, color: 'var(--trello-text-muted)' }}>
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
            <div key={d} style={{ padding: '8px 10px', textAlign: 'center' }}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7" style={{ gap: 1, background: 'var(--trello-list-bg)' }}>
          {cells.map((c, i) => {
            const inMonth = c.date.getMonth() === cursor.getMonth();
            const isToday = sameDay(c.date, new Date());
            
            const dateStr = c.date.getFullYear() + '-' + 
                            String(c.date.getMonth() + 1).padStart(2, '0') + '-' + 
                            String(c.date.getDate()).padStart(2, '0');
            const dayMarkings = dateMarkings.filter(m => m.date === dateStr);
            const isMarked = dayMarkings.length > 0;
            const topBorderColor = isMarked ? dayMarkings[0].tag_color : (isToday ? '#1a73e8' : 'transparent');

            return (
              <div key={i} className="group/day relative flex flex-col justify-between" style={{
                background: 'var(--trello-card-bg)', minHeight: 110, padding: 6,
                opacity: inMonth ? 1 : 0.45,
                borderTop: `4px solid ${topBorderColor}`,
                borderLeft: isToday ? '1px solid #1a73e8' : 'none',
                borderRight: isToday ? '1px solid #1a73e8' : 'none',
                borderBottom: isToday ? '1px solid #1a73e8' : 'none',
              }} data-testid={`cal-day-${c.date.toISOString().slice(0,10)}`}>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1 overflow-hidden flex-1">
                      <span style={{ fontSize: 11, fontWeight: (isToday || isMarked) ? 800 : 500, color: isToday ? '#1a73e8' : 'var(--trello-text)' }}>{c.date.getDate()}</span>
                      <div className="flex flex-wrap gap-0.5 max-w-[65px] overflow-hidden">
                        {dayMarkings.map(m => (
                          <span key={m.tag_id} className="text-[7px] font-black uppercase px-0.5 py-0.2 rounded text-white truncate max-w-[32px] block" style={{ background: m.tag_color }} title={m.tag_name}>
                            {m.tag_name}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button 
                        onClick={() => setActiveMarkDropdownDate(activeMarkDropdownDate === dateStr ? null : dateStr)}
                        className={`p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700 transition-all ${isMarked ? 'opacity-100' : 'opacity-0 group-hover/day:opacity-100'}`}
                        title="Mark day with activity tags"
                      >
                        <Tag size={10} />
                      </button>
                      <button 
                        onClick={() => handleOpenAddModal(c.date)}
                        className="opacity-0 group-hover/day:opacity-100 p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-700 transition-all"
                        title="Add task for this day"
                      >
                        <Plus size={10} />
                      </button>
                    </div>

                    {/* Marketing Custom Multi-Tag Dropdown Menu */}
                    {activeMarkDropdownDate === dateStr && (
                      <>
                        <div className="fixed inset-0 z-30 cursor-default" onClick={() => setActiveMarkDropdownDate(null)} />
                        <div className="absolute right-0 mt-6 bg-white rounded-xl shadow-xl border border-slate-150 py-1.5 z-40 w-44 text-[10px] text-left font-bold text-slate-700 animate-fade-in">
                          <div className="px-2.5 py-1 text-slate-400 text-[8px] uppercase tracking-wider">Select Tags:</div>
                          <div className="max-h-36 overflow-y-auto px-1.5 py-1 space-y-1">
                            {marketingTags.map((tag) => {
                              const isSelected = dayMarkings.some(dm => dm.tag_id === tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => onUpdateMarking(c.date, tag.id, isSelected ? 'remove' : 'add')}
                                  className="w-full px-2 py-1.5 rounded-lg flex items-center justify-between text-slate-700 hover:bg-slate-50 transition-colors"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: tag.color }} />
                                    <span className="truncate max-w-[100px]">{tag.name}</span>
                                  </div>
                                  {isSelected && (
                                    <span className="text-[10px] text-blue-600 font-extrabold">✓</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          <div className="border-t border-slate-100 mt-1.5 pt-1.5 px-2">
                            <button
                              type="button"
                              onClick={() => {
                                setManageModalOpen(true);
                                setActiveMarkDropdownDate(null);
                              }}
                              className="w-full text-center py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors text-[9px] font-bold"
                            >
                              Manage / Create Tags
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="space-y-1">
                    {c.tasks.slice(0, 3).map((t) => {
                      const actStyle = getActivityStyle(t.title);
                      const bg = actStyle ? actStyle.bg : (t.label_color || '#EBECF0');
                      const color = actStyle ? actStyle.color : (t.label_color ? '#fff' : 'var(--trello-text)');
                      return (
                        <button key={t.id} onClick={() => onOpenTask(t.id)} data-testid={`cal-task-${t.id}`}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                            background: bg,
                            color: color,
                            fontSize: 10, fontWeight: 700, padding: '4px 6px', borderRadius: 6,
                            textDecoration: t.completed ? 'line-through' : 'none',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            marginBottom: 4
                          }}
                        >
                          {actStyle ? `[${actStyle.tag}] ` : ''}{t.title}
                        </button>
                      );
                    })}
                    {c.tasks.length > 3 && <div style={{ fontSize: 9, color: 'var(--trello-text-muted)', fontWeight: 600 }}>+{c.tasks.length - 3} more</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddModal && selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-150 p-6 w-full max-w-sm animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-800">Add Task for {selectedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Task Title</label>
                <input 
                  type="text"
                  placeholder="e.g., SEO Keyword Research or Blog Draft..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  className="w-full p-2.5 border border-slate-200 rounded-xl text-xs outline-none focus:border-blue-500 font-medium text-slate-800"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Target Board List</label>
                <select 
                  value={selectedListId}
                  onChange={(e) => setSelectedListId(e.target.value)}
                  className="w-full p-2.5 border border-slate-200 rounded-xl text-xs outline-none focus:border-blue-500 font-bold text-slate-700 bg-white"
                >
                  {lists.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button 
                onClick={() => setShowAddModal(false)}
                className="px-3.5 py-2 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreateTask}
                disabled={!newTaskTitle.trim() || !selectedListId}
                className="px-3.5 py-2 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors disabled:opacity-50"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Custom Tags Dialog Modal */}
      {manageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-150 p-6 w-full max-w-md animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-slate-800">Manage Marketing Tags</h3>
              <button 
                onClick={() => setManageModalOpen(false)}
                className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Create Tag Form */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">Create New Tag</h4>
                <div className="flex gap-2 mb-3">
                  <input 
                    type="text"
                    placeholder="Tag Name (e.g., Email Marketing)"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    className="flex-1 p-2 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500 font-bold text-slate-700 bg-white"
                  />
                  <button
                    onClick={handleCreateTagClick}
                    disabled={!newTagName.trim()}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs transition-colors disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex gap-2.5 items-center">
                  <span className="text-[9px] font-bold text-slate-400 uppercase">Color:</span>
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewTagColor(c)}
                      className={`w-5 h-5 rounded-full border transition-all ${newTagColor === c ? 'scale-125 border-slate-800' : 'border-transparent'}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>

              {/* Existing Tags List */}
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 mb-1">Existing Tags</h4>
                {marketingTags.map(tag => {
                  const isEditing = editingTagId === tag.id;
                  return (
                    <div key={tag.id} className="flex items-center justify-between p-2.5 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
                      {isEditing ? (
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="flex gap-2">
                            <input 
                              type="text"
                              value={editTagName}
                              onChange={(e) => setEditTagName(e.target.value)}
                              className="flex-1 p-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500 font-bold text-slate-700 bg-white"
                            />
                            <button
                              onClick={() => handleSaveEditTag(tag.id)}
                              className="px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg text-xs transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingTagId(null)}
                              className="px-2.5 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg text-xs transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                          <div className="flex gap-2 items-center">
                            <span className="text-[8px] font-bold text-slate-400 uppercase">Color:</span>
                            {PRESET_COLORS.map(c => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setEditTagColor(c)}
                                className={`w-4 h-4 rounded-full border transition-all ${editTagColor === c ? 'scale-125 border-slate-800' : 'border-transparent'}`}
                                style={{ background: c }}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ background: tag.color }} />
                            <span className="text-xs font-bold text-slate-700">{tag.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleStartEditTag(tag)}
                              className="px-2 py-1 text-[9px] font-extrabold text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => onDeleteTag(tag.id)}
                              className="px-2 py-1 text-[9px] font-extrabold text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
